import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Cpm = () => {
  // State variables for CPM calculation
  const [advertisingCost, setAdvertisingCost] = useState(0); // Total advertising cost
  const [impressions, setImpressions] = useState(0); // Total impressions
  const [cpm, setCpm] = useState(0); // Calculated CPM (Cost Per 1000 Impressions)

  // Event handlers to update state values
  const handleAdvertisingCostChange = (e) => setAdvertisingCost(parseFloat(e.target.value) || 0);
  const handleImpressionsChange = (e) => setImpressions(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate CPM when any input changes
  useEffect(() => {
    // Calculate CPM = (Advertising Cost / Impressions) * 1000
    if (impressions > 0) {
      const calculatedCpm = (advertisingCost / impressions) * 1000;
      setCpm(calculatedCpm);
    } else {
      setCpm(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [advertisingCost, impressions]);

  return (
    <Layout title="Advertising Cost Per Impression (CPM) Calculator" path="cpm">
      <section className="section-padding" id="cpm-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* CPM Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Advertising Cost Per Impression (CPM) Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Advertising Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="advertisingCost" className="form-label">
                        Advertising Cost
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Advertising Cost:</strong> Enter the total amount spent on advertising for the campaign. This includes all related expenses."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="advertisingCost"
                        placeholder="Enter total advertising cost"
                        value={advertisingCost || ""}
                        onChange={handleAdvertisingCostChange}
                      />
                    </div>

                    {/* Impressions Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="impressions" className="form-label">
                        Impressions
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Impressions:</strong> Enter the total number of impressions (views or exposures) that the advertisement received."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="impressions"
                        placeholder="Enter total impressions"
                        value={impressions || ""}
                        onChange={handleImpressionsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Cost Per 1,000 Impressions (CPM):{" "}
                        <span className="fw-bold ms-3">
                          {cpm === 0 ? "0.00" : cpm.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Advertising Cost Per Impression (CPM) Calculator
                </h3>

                {/* What is an Advertising Cost Per Impression (CPM) Calculator? */}
                <h4 className="mt-3 mb-3">What is an Advertising Cost Per Impression (CPM) Calculator?</h4>
                <p>
                  The **Advertising Cost Per Impression (CPM) Calculator** helps digital marketers calculate the cost of 1,000 impressions in an advertising campaign. This metric, known as CPM, is crucial for evaluating ad spend efficiency and optimizing ad budgets.
                </p>

                {/* Why Use an Advertising Cost Per Impression (CPM) Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Advertising Cost Per Impression (CPM) Calculator?</h4>
                <p>
                  The CPM Calculator is vital for businesses aiming to track the effectiveness of their advertising campaigns. It helps marketers compare costs across platforms and refine their ad spending strategies to maximize reach and minimize waste.
                </p>

                {/* Benefits of Using an Advertising Cost Per Impression (CPM) Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Advertising Cost Per Impression (CPM) Calculator</h4>
                <ul>
                  <li><strong>Useful for tracking ad spend efficiency:</strong> By calculating the cost per 1,000 impressions, marketers can assess if the ad spend is delivering good value.</li>
                  <li><strong>Helps in planning ad budgets and maximizing reach:</strong> The CPM allows businesses to allocate advertising budgets more effectively and optimize their marketing strategies.</li>
                  <li><strong>Assists in comparing costs across ad platforms:</strong> Different platforms may have different CPM rates, and this calculator allows for easy comparison to determine the most cost-effective option.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Advertising Cost Per Impression (CPM) Calculator?</h4>
                <p>
                  The CPM Calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Digital marketers:</strong> To track the cost of advertising campaigns and maximize ad reach.</li>
                  <li><strong>Media agencies:</strong> For evaluating different ad campaign costs and efficiency.</li>
                  <li><strong>E-commerce businesses:</strong> To assess advertising costs and improve their marketing strategies.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">Why is CPM Important in Advertising?</h4>
                <p>
                  CPM is a critical metric for advertisers because it shows the cost of reaching 1,000 potential customers with an ad. Understanding CPM helps businesses gauge the cost-effectiveness of their advertising and optimize their budget allocation.
                </p>

                <h4 className="mt-4 mb-3">How Do You Optimize CPM for Advertising Campaigns?</h4>
                <p>
                  To optimize CPM, businesses should focus on targeting the right audience, selecting the most cost-efficient platforms, and continually refining their ads to improve engagement. Tracking and comparing CPM across platforms ensures businesses are spending their ad budgets effectively.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Cpm;
